import { useShadcnNotification } from '@src/components/ui/notification';

import { useQueryFetch } from '@shared/hooks/useQueryFetch';
import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import {
    getReceptionSchedules,
    useReceptionSchedules,
} from '@shared/hooks/useReceptionSchedules';
import type { ReceptionScheduleVm } from '@shared/types/process';

import {
    getReceptionCalendarQueryRange,
    receptionSlotOverlapsRange,
    ShadcnButton,
    ShadcnModal,
    ShadcnReceptionCalendar,
    type TReceptionCalendarView,
} from '@shadcn-features/core';
import dayjs from 'dayjs';
import { RotateCw } from 'lucide-react';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

export interface ReceptionScheduleSlotModalRef {
    open: (
        buttonId: string,
        onAfterReserveSlot: () => void,
        organizationId?: string,
    ) => void;
}

interface ReceptionSlotDto {
    id: string;
    dateFrom: string;
    timeFrom: string;
    dateTo: string;
    timeTo: string;
    capacity: number;
    occupiedSeats: number;
    description: string;
}

interface ReceptionBookingForAccountVm {
    buttonId: string;
    booking: ReceptionScheduleVm['booking'];
}

interface ReceptionScheduleSlotModalProps {
    serviceId: string;
    onBookingChanged?: (
        receptionSchedules: ReceptionScheduleVm[],
    ) => void | Promise<void>;
}

type ReceptionSlotsResponse =
    | ReceptionSlotDto[]
    | {
          itemList?: ReceptionSlotDto[];
          data?: ReceptionSlotDto[] | { itemList?: ReceptionSlotDto[] };
      };

const RECEPTION_SCHEDULE_MODAL_Z_INDEX = 1400;

const getSlots = (response?: ReceptionSlotsResponse) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.data)) return response.data;
    return response.data?.itemList ?? response.itemList ?? [];
};

const getBookedSlot = (
    booking: ReceptionScheduleVm['booking'],
): ReceptionSlotDto | null => {
    if (!booking) return null;

    return {
        id: booking.slotId,
        dateFrom: booking.dateFrom,
        timeFrom: booking.timeFrom,
        dateTo: booking.dateTo,
        timeTo: booking.timeTo,
        capacity: 1,
        occupiedSeats: 1,
        description: '',
    };
};

export const ReceptionScheduleSlotModal = forwardRef<
    ReceptionScheduleSlotModalRef,
    ReceptionScheduleSlotModalProps
>(({ serviceId, onBookingChanged }, ref) => {
    const { t } = useTranslation();
    const notification = useShadcnNotification();
    const [open, setOpen] = useState(false);
    const [calendarDate, setCalendarDate] = useState(() => new Date());
    const [view, setView] = useState<TReceptionCalendarView>('agenda');
    const [selectedSlot, setSelectedSlot] = useState<ReceptionSlotDto | null>(
        null,
    );
    const [buttonId, setButtonId] = useState<string | null>(null);
    const onAfterReserveSlotRef = useRef<() => void>(() => void 0);

    const queryRange = useMemo(
        () => getReceptionCalendarQueryRange(view, calendarDate),
        [calendarDate, view],
    );

    const resetModalState = () => {
        setSelectedSlot(null);
        setButtonId(null);
        setOpen(false);
    };

    useEffect(() => {
        setSelectedSlot(null);
        setButtonId(null);
        setOpen(false);
    }, [serviceId]);

    useImperativeHandle(ref, () => ({
        open(nextButtonId, onAfterReserveSlot) {
            if (!nextButtonId?.trim()) return;

            const now = new Date();
            setCalendarDate(now);
            setView('agenda');
            setSelectedSlot(null);
            setButtonId(nextButtonId);
            setOpen(true);
            onAfterReserveSlotRef.current = onAfterReserveSlot;
        },
    }));

    const {
        data: slotsResponse,
        isFetching,
        refetch,
    } = useQueryFetch<ReceptionSlotsResponse>(
        'flow/Account/Services/GetReceptionSlots',
        {
            params: {
                serviceId,
                buttonId,
                dateFrom: queryRange.dateFrom,
                dateTo: queryRange.dateTo,
            },
            enabled: open && !!serviceId && !!buttonId,
        },
    );

    const reserveSlot = useQueryMutation<
        {
            serviceId: string;
            buttonId: string;
            receptionSlotId: string;
            slotDate: string;
        },
        ReceptionBookingForAccountVm
    >('flow/Account/Services/ReserveReceptionSlot', 'post', {
        invalidateOnSuccess: [
            ['flow/Account/Services/GetReceptionSchedules'],
            ['flow/Account/Services/GetReceptionSlots'],
        ],
    });

    const slots = useMemo(() => getSlots(slotsResponse), [slotsResponse]);

    const {
        schedules: receptionSchedules,
        refetch: refetchReceptionSchedules,
    } = useReceptionSchedules(serviceId, open && !!buttonId);

    const bookedSlot = useMemo(() => {
        if (!buttonId) return null;

        return getBookedSlot(
            receptionSchedules.find(
                (schedule) => schedule.buttonId === buttonId,
            )?.booking ?? null,
        );
    }, [buttonId, receptionSchedules]);

    const calendarSlots = useMemo(() => {
        if (!bookedSlot || slots.some((slot) => slot.id === bookedSlot.id)) {
            return slots;
        }

        return [...slots, bookedSlot];
    }, [bookedSlot, slots]);

    useEffect(() => {
        if (!open || !bookedSlot) return;

        const bookedDate = dayjs(bookedSlot.dateFrom).toDate();
        setSelectedSlot(bookedSlot);
        setCalendarDate(bookedDate);
        setView('agenda');
    }, [bookedSlot, open]);

    const handleCalendarDateChange = (date: Date) => {
        setSelectedSlot(null);
        setCalendarDate(date);
    };

    const handleViewChange = (nextView: TReceptionCalendarView) => {
        const nextRange = getReceptionCalendarQueryRange(
            nextView,
            calendarDate,
        );
        setView(nextView);
        if (
            selectedSlot &&
            !receptionSlotOverlapsRange(selectedSlot, nextRange)
        ) {
            setSelectedSlot(null);
        }
    };

    const handleSave = async () => {
        if (!selectedSlot || !buttonId) return;

        try {
            await reserveSlot.mutateAsync({
                serviceId,
                buttonId,
                receptionSlotId: selectedSlot.id,
                slotDate: selectedSlot.dateFrom,
            });
            const schedulesResponse = await refetchReceptionSchedules();
            await onBookingChanged?.(
                getReceptionSchedules(schedulesResponse.data),
            );
            onAfterReserveSlotRef.current();
            resetModalState();
        } catch {
            setSelectedSlot(null);
            notification.warning({
                message: t(
                    'translation:receptionScheduleSlotNoLongerAvailable',
                ),
                description: t(
                    'translation:receptionScheduleChooseAnotherSlot',
                ),
            });
            await refetch();
        }
    };

    const handleClose = () => {
        if (reserveSlot.isPending) return;
        resetModalState();
    };

    return (
        <ShadcnModal
            destroyOnClose
            open={open}
            onClose={handleClose}
            title={
                <div className='flex flex-wrap items-center gap-2'>
                    <span className='text-[28px] leading-9 font-semibold text-(--color-card-text) sm:text-[32px]'>
                        {t('translation:receptionSchedule')}
                    </span>
                    <ShadcnButton
                        type='button'
                        size='sm'
                        variant='outline'
                        height={36}
                        disabled={isFetching || reserveSlot.isPending}
                        onClick={() => void refetch()}
                    >
                        <RotateCw
                            aria-hidden='true'
                            className={isFetching ? 'animate-spin' : undefined}
                        />
                        {t('translation:refresh')}
                    </ShadcnButton>
                </div>
            }
            width='min(1428px, calc(100vw - 32px))'
            zIndex={RECEPTION_SCHEDULE_MODAL_Z_INDEX}
            className='max-h-[calc(100vh-24px)] rounded-[20px] border-0 shadow-[3px_3px_16px_rgba(0,0,0,0.08)]'
            styles={{
                content: {
                    height: 'min(860px, calc(100vh - 24px))',
                },
                header: {
                    borderBottom: 0,
                    padding: '28px 36px 12px',
                },
                body: {
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    overflowY: 'hidden',
                    padding: '0 36px',
                },
                footer: {
                    borderTop: 0,
                    padding: '24px 36px 28px',
                },
            }}
            footer={
                <div className='grid w-full grid-cols-2 gap-3'>
                    <ShadcnButton
                        type='button'
                        size='md'
                        variant='outline'
                        className='w-full shadow-sm'
                        height={40}
                        disabled={reserveSlot.isPending}
                        onClick={handleClose}
                    >
                        {t('translation:cancel')}
                    </ShadcnButton>
                    <ShadcnButton
                        type='button'
                        size='md'
                        variant='brandGreen'
                        className='w-full'
                        height={40}
                        disabled={!selectedSlot}
                        loading={reserveSlot.isPending}
                        onClick={() => void handleSave()}
                    >
                        {t('translation:save')}
                    </ShadcnButton>
                </div>
            }
        >
            <div className='flex h-full min-h-0 w-full flex-col'>
                <ShadcnReceptionCalendar
                    key={open ? 'open' : 'closed'}
                    slots={calendarSlots}
                    selectedDate={calendarDate}
                    selectedSlotId={selectedSlot?.id}
                    view={view}
                    loading={isFetching}
                    selectionPending={reserveSlot.isPending}
                    onDateChange={handleCalendarDateChange}
                    onViewChange={handleViewChange}
                    onSelect={setSelectedSlot}
                />
            </div>
        </ShadcnModal>
    );
});

ReceptionScheduleSlotModal.displayName = 'ReceptionScheduleSlotModal';
